/* eslint-disable react/no-this-in-sfc */
import classNames from "components/ui/classNames";
import { TextTooltip } from "components/ui/Tooltip";
import formatDistance from "date-fns/formatDistance";
import enUS from "date-fns/locale/en-US";
import ptBR from "date-fns/locale/pt-BR";
import parseISO from "date-fns/parseISO";
import { useIntl } from "react-intl";
import type { ITrailingReturnReferencePrice } from "types/graphqlTypes";

interface DrawdownBoxProps {
  title: string;
  tickerSymbol?: string;
  tradingCurrency: string;
  allTimeHigh: ITrailingReturnReferencePrice;
  drawdownFromAllTimeHigh: number;
  className?: string;
  tickerSymbolClassName?: string;
  style?: React.CSSProperties;
}
export default function DrawdownBox({
  title,
  tradingCurrency,
  allTimeHigh,
  drawdownFromAllTimeHigh,
  tickerSymbol,
  className,
  tickerSymbolClassName,
  style,
}: DrawdownBoxProps) {
  const intl = useIntl();

  const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const valueFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: tradingCurrency || "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    minimumSignificantDigits: 2,
  });

  const isBigValue = drawdownFromAllTimeHigh >= 10000;

  return (
    <div
      key={tickerSymbol}
      style={style}
      className={classNames(
        "group relative inline-flex w-full flex-col rounded-xl p-2.5 sm:max-w-36",
        "bg-slate-50/80",
        className,
        {
          "bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400": drawdownFromAllTimeHigh < 0,
          "text-slate-600 dark:text-slate-400": drawdownFromAllTimeHigh >= 0,
        },
      )}
    >
      <div className="space-x-2">
        <strong className="font-mono text-2xl font-bold">
          {isBigValue
            ? `${valueFormatter.format(drawdownFromAllTimeHigh)} %`
            : percentageFormatter.format(drawdownFromAllTimeHigh)}
        </strong>
        <span className="font-mono text-xs font-medium">
          {moneyFormatter.format(roundToDecimals(allTimeHigh.close, 2))}
        </span>
      </div>

      <div className="mt-1.5 space-y-1">
        <TextTooltip
          content={formatDistance(new Date(), parseISO(allTimeHigh.tradeDate), {
            locale: intl.locale === "pt-BR" ? ptBR : enUS,
          })}
        >
          <p className="space-x-1 text-xs font-normal text-slate-600 dark:text-slate-400">
            {intl.formatDate(parseISO(allTimeHigh.tradeDate), {
              day: "numeric",
              month: "numeric",
              year: "2-digit",
            })}
          </p>
        </TextTooltip>
        <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">{title}</h4>
      </div>

      {tickerSymbol && (
        <span
          className={classNames(
            "mt-2 rounded-md bg-zinc-100 p-1 text-center font-mono text-xs font-bold tracking-wider text-slate-700 dark:bg-zinc-900 dark:text-slate-300",
            tickerSymbolClassName,
            {
              "bg-rose-100 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400": drawdownFromAllTimeHigh < 0,
              "bg-slate-50 text-slate-600 dark:bg-slate-900/20 dark:text-slate-400": drawdownFromAllTimeHigh >= 0,
            },
          )}
        >
          {tickerSymbol}
        </span>
      )}
    </div>
  );
}

function roundToDecimals(value: number, decimals: number): number {
  return Math.round(value * 10 ** decimals) / 10 ** decimals;
}
